import localforage from 'localforage';
import { v4 as uuidv4 } from 'uuid';
import { useAppStateLoadingCountStore } from '../../../common/store/AppStateStore.zustand';
import {
  InvestmentIncome,
  InvestmentIncomeRequest,
} from '../type/InvestmentIncome.type';

export const investmentIncomeStore = localforage.createInstance({
  name: 'honeymoonsalad',
  storeName: 'investmentIncomeStore',
});

const normalizeInvestmentIncome = (value: unknown): InvestmentIncome | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const legacyNetAmount =
    Number(record.grossAmount || 0) -
    Number(record.taxAmount || 0) -
    Number(record.feeAmount || 0);
  return {
    id: String(record.id || ''),
    owner: record.owner as InvestmentIncome['owner'],
    assetName: String(record.assetName || ''),
    paymentDate: String(record.paymentDate || ''),
    amount: Number(record.amount ?? legacyNetAmount),
    memo: String(record.memo || ''),
    timestamp: Number(record.timestamp || 0),
  };
};

export const useInvestmentIncomeStore = () => {
  const { addCount, minusCount } = useAppStateLoadingCountStore();

  const exportInvestmentIncomeStore = async () => {
    const keys = await investmentIncomeStore.keys();
    return Promise.all(
      keys.map(async (key) => ({ key, value: await investmentIncomeStore.getItem(key) })),
    );
  };

  const importInvestmentIncomeStore = async (entries: { key: string; value: unknown }[]) => {
    await investmentIncomeStore.clear();
    await Promise.all(entries.map(({ key, value }) => investmentIncomeStore.setItem(key, value)));
    return true;
  };

  const clearInvestmentIncomeStore = async () => {
    await investmentIncomeStore.clear();
    return true;
  };

  const insertInvestmentIncomeToStore = async (request: InvestmentIncomeRequest) => {
    addCount();
    try {
      const id = uuidv4().toString();
      await investmentIncomeStore.setItem(id, { ...request, id });
      return { data: id };
    } finally {
      minusCount();
    }
  };

  const getInvestmentIncomeListFromStore = async () => {
    addCount();
    try {
      const keys = await investmentIncomeStore.keys();
      const list = await Promise.all(keys.map((key) => investmentIncomeStore.getItem(key)));
      return {
        data: list
          .map(normalizeInvestmentIncome)
          .filter((income): income is InvestmentIncome => Boolean(income))
          .sort((a, b) => b.paymentDate.localeCompare(a.paymentDate)),
      };
    } finally {
      minusCount();
    }
  };

  const updateInvestmentIncomeAtStore = async (
    id: string,
    request: InvestmentIncomeRequest,
  ) => {
    addCount();
    try {
      await investmentIncomeStore.setItem(id, { ...request, id });
      return { data: id };
    } finally {
      minusCount();
    }
  };

  const deleteInvestmentIncomeAtStore = async (id: string) => {
    addCount();
    try {
      const income = await investmentIncomeStore.getItem<InvestmentIncome>(id);
      await investmentIncomeStore.removeItem(id);
      return { data: income };
    } finally {
      minusCount();
    }
  };

  return {
    exportInvestmentIncomeStore,
    importInvestmentIncomeStore,
    clearInvestmentIncomeStore,
    insertInvestmentIncomeToStore,
    getInvestmentIncomeListFromStore,
    updateInvestmentIncomeAtStore,
    deleteInvestmentIncomeAtStore,
  };
};
